<script lang="ts">
  import { Loader2, Database, Search, BarChart3 } from 'lucide-svelte';
  import { onDestroy, onMount } from 'svelte';
  import { _ } from 'svelte-i18n';

  /**
   * One fixed message instead of the cycle.
   *
   * The cycle describes the stages of a search. Opening a shared link is one
   * wait with one meaning, and naming it — "opening the shared selection" —
   * tells the recipient what they are waiting for, which four rotating guesses
   * about a search they did not start would not.
   */
  export let label = '';

  /**
   * The animation shown while a search runs.
   *
   * Its four messages were written in English and never translated, so a German
   * customer — which is nearly all of them — watched "Loading data…" and
   * "Almost ready!" for the length of every search. They are keys now.
   *
   * Reactive, not read once: the locale can change under a running search.
   */
  $: messages = [
    { text: $_('loading.data'), icon: Database },
    { text: $_('loading.searching'), icon: Search },
    { text: $_('loading.analyzing'), icon: BarChart3 },
    { text: $_('loading.almost'), icon: Loader2 }
  ];

  let index = 0;
  let interval: ReturnType<typeof setInterval> | undefined;

  onMount(() => {
    interval = setInterval(() => {
      index = (index + 1) % messages.length;
    }, 3000);
  });

  onDestroy(() => {
    clearInterval(interval);
  });

  $: currentMessage = messages[index];
</script>

<!--
  White, like the area it covers.

  This filled itself with `bg-gray-100`, which was invisible back when the
  content well behind it was grey. The well is white now, so every wait turned
  the whole data view grey and then back again — a flash that reads as the
  screen breaking rather than as the screen working.

  All four callers cover a white surface: the results area, the shell while it
  boots, the shared-selection restore, and the similar-companies panel.
-->
<div class="flex h-full w-full items-center justify-center bg-surface-panel">
  <div class="flex flex-col items-center space-y-6 p-8">
    <!-- Main loading animation -->
    <div class="relative">
      <!-- Background circle -->
      <div class="absolute inset-0 animate-pulse rounded-full bg-accent-100"></div>

      <!-- Main spinner -->
      <div
        class="relative flex h-24 w-24 items-center justify-center rounded-full bg-white shadow-lg"
      >
        <Loader2 class="h-10 w-10 animate-spin text-accent-600" />
      </div>

      <!-- Floating dots -->
      <div
        class="absolute -right-2 -top-2 h-4 w-4 animate-bounce rounded-full bg-accent-500"
        style="animation-delay: 0s;"
      ></div>
      <div
        class="absolute -bottom-2 -left-2 h-3 w-3 animate-bounce rounded-full bg-accent-500"
        style="animation-delay: 0.5s;"
      ></div>
      <div
        class="absolute -left-2 -top-2 h-2 w-2 animate-bounce rounded-full bg-accent-500"
        style="animation-delay: 1s;"
      ></div>
    </div>

    <!-- Dynamic message with icon -->
    <div class="flex flex-col items-center space-y-3 transition-all duration-500">
      <div
        class="flex items-center space-x-3 rounded-surface border border-hairline bg-white p-4 shadow-sm"
      >
        <svelte:component
          this={label ? Loader2 : currentMessage.icon}
          class="h-5 w-5 text-accent-600 {label || currentMessage.icon === Loader2
            ? 'animate-spin'
            : ''}"
        />
        <span class="text-lg font-medium text-gray-800">{label || currentMessage.text}</span>
      </div>

      <!-- Progress indicator: the stages of a search, so not shown for a
           caller that named its own single wait. -->
      {#if !label}
        <div class="flex space-x-2">
          <!-- Named, because `_` is the translation store in this file now. -->
          {#each messages as message, i (message.text)}
            <div
              class="h-2 w-2 rounded-full transition-all duration-300 {i === index
                ? 'scale-125 bg-accent-600'
                : 'bg-hairline-strong'}"
            ></div>
          {/each}
        </div>
      {/if}
    </div>

    <!-- Subtle company info -->
    <div class="mt-4 text-center text-xs text-gray-500">
      <p>ainova</p>
    </div>
  </div>
</div>
